import type { ApplicationRow, VenueRow } from './deskPrefill'
import { buildVenueIndex, matchVenueLayerA, venueIdsForText, venueNameKey } from './venueNameKey'
import type { VenueAliasRow, VenueKeyRow } from './venueNameKey'

// Mapped here rather than imported from deskPrefill: deskPrefill pulls in the
// ESM-only franc-min, and this module stays free of it (pure, cheap to test).
const keyRows = (venues: readonly VenueRow[]): VenueKeyRow[] =>
  venues.map((venue) => ({ id: venue.id, name: venue.name ?? '', city_id: venue.city_id }))

/**
 * The desk teaches the editor (Phase 33.4 layer A.3) and flags its model guess.
 *
 * - `rememberAliasOffer` (D-14, D-15, plus the candidate's-town check from
 *   the 33.4 code review): whether the candidate screen shows the
 *   "Remember '<text>' as <venue>" checkbox under the venue field, and with what.
 *   Every D-15 condition must hold; the offer hides as soon as one stops holding
 *   (she changes or clears the venue). Aliases that failed to load give no offer
 *   (P-12): the town's names cannot be checked for a conflict without them.
 * - `showSuggestedVenueHint` (D-24): the "Suggested venue: check it" badge, shown
 *   while the editor's model venue is still the selected one.
 *
 * The checkbox is default off (D-14) and its state lives in the desk, not in the
 * form values. The write itself (D-16) happens on approve, in the candidate
 * screen. Pure: no Supabase, no React, no i18n, never a model.
 */

/** D-10: an alias is 2..120 characters once trimmed (the 0039 check). */
export const ALIAS_MIN_LENGTH = 2
export const ALIAS_MAX_LENGTH = 120

export type RememberAliasOffer = { text: string; venueId: number; venueName: string }

export type RememberAliasInput = {
  application: Pick<ApplicationRow, 'editor_version' | 'venue_id' | 'venue_match' | 'event_venue_location'>
  /** The listed venue selected in the form, null on the custom venue. */
  selectedVenue: VenueRow | null
  venues: VenueRow[]
  /** null = the alias lookup failed (P-12). */
  aliases: VenueAliasRow[] | null
  /**
   * The candidate's town as the form has it now: the editor's resolved
   * `city_id` until she changes the town. The selected venue must be in it
   * (code review, D-15 town defect): an alias is read per its venue's town, so
   * remembering a text under another town's venue teaches nothing. null (no
   * resolved town) gives no offer.
   */
  townCityId: number | null
}

export const rememberAliasOffer = ({
  application,
  selectedVenue,
  venues,
  aliases,
  townCityId
}: RememberAliasInput): RememberAliasOffer | null => {
  if (aliases === null) return null
  if (application.editor_version === null) return null
  if (application.venue_id !== null && application.venue_match !== 'model') return null
  if (selectedVenue === null || selectedVenue.city_id === null) return null
  // The venue must be in the candidate's town (W2 in the 33.4 desk walk: venue
  // 96, Ermidas-Sado, was offered on an Ourique candidate).
  if (townCityId === null || selectedVenue.city_id !== townCityId) return null
  // A nameless listed venue cannot be named in the label.
  const venueName = (selectedVenue.name ?? '').trim()
  if (venueName === '') return null

  const text = (application.event_venue_location ?? '').trim()
  if (venueNameKey(text) === '') return null
  if (text.length < ALIAS_MIN_LENGTH || text.length > ALIAS_MAX_LENGTH) return null

  const cityId = selectedVenue.city_id
  const listed = venues.some((venue) => venue.id === selectedVenue.id) ? venues : [...venues, selectedVenue]
  const index = buildVenueIndex(keyRows(listed), aliases)

  // D-05 step 1: already this venue's name or alias -> nothing to remember.
  // Two or more venues on the key -> ambiguous in the town.
  const named = venueIdsForText(text, cityId, index)
  if (named.ids.includes(selectedVenue.id)) return null
  if (named.ids.length >= 2) return null

  // The full D-05 match lands on another venue of the town: the alias would
  // make that name ambiguous. On this venue (through a split part, IN-B09),
  // layer A already links the text: an alias would add nothing.
  const match = matchVenueLayerA(text, cityId, index)
  if (match !== null) return null

  return { text, venueId: selectedVenue.id, venueName }
}

/** D-24: true while the editor's model venue is still the one selected. */
export const showSuggestedVenueHint = (
  application: Pick<ApplicationRow, 'venue_match' | 'venue_id'>,
  selectedVenueId: number | null
): boolean =>
  application.venue_match === 'model' &&
  application.venue_id !== null &&
  selectedVenueId === application.venue_id
